import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bc-teal focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-bc-teal text-white hover:bg-bc-teal-dark hover:shadow-glow',
        secondary: 'bg-bc-navy text-white hover:bg-bc-navy-light',
        destructive: 'bg-error text-white hover:bg-red-600',
        outline: 'border-2 border-bc-teal bg-transparent text-bc-teal hover:bg-bc-teal hover:text-white hover:shadow-glow',
        'outline-destructive':
          'border-2 border-error bg-transparent text-error hover:bg-error hover:text-white',
        'outline-success':
          'border-2 border-success bg-transparent text-success hover:bg-success hover:text-white',
        ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-bc-navy',
        link: 'text-bc-teal underline-offset-4 hover:underline',
        success: 'bg-success text-white hover:bg-green-600',
      },
      size: {
        // All sizes ensure 44px minimum height for touch targets
        default: 'h-11 px-6 py-2 min-h-[44px]',
        sm: 'h-9 px-4 text-xs min-h-[36px] md:min-h-[44px]',
        lg: 'h-12 px-8 text-base min-h-[48px]',
        icon: 'h-11 w-11 min-h-[44px] min-w-[44px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

function Button({ className, variant, size, asChild = false, loading, disabled, children, ref, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          <span className="sr-only">Loading</span>
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
